import React, { ComponentProps, useMemo } from 'react';
import styles from '../../styles/partials/_tabs.css';
import styled, { css } from 'styled-components';
import { checkPermission } from '../../utils/OperationPermissions';
import { makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { useFullUserDetails } from '../../utils/hooks';

type IColourMixinType = 'success' | 'warning' | 'danger' | 'info';

const colorMixin = (props: { type?: IColourMixinType } & { theme?: any }) => {
  let color;
  switch (props.type) {
    case 'success':
      color = p4Tokens.statusFlagSuccess;
      break;
    case 'warning':
      color = p4Tokens.statusFlagWarning;
      break;
    case 'danger':
      color = p4Tokens.statusFlagDanger;
      break;
    case 'info':
      color = p4Tokens.statusFlagInfo;
      break;
    default:
      color = p4Tokens.statusFlagBasic;
  }

  return css`
    background-color: ${color};
  `;
};

export const Badge = styled.div<{ type?: IColourMixinType }>`
  display: inline-block;
  background-color: #2196f3;
  color: white;
  margin-left: 2px;
  padding: 0 4px;
  border-radius: 3px;
  ${(props: { type?: IColourMixinType }) => colorMixin(props)}
`;

type ITabPropsFromTabPanel = {
  activateTab: (activeTabId: string, clicked: boolean) => void;
  isActiveTab: boolean;
  className: string;
};
export type ITabProps<TabId extends unknown> = ITabPropsFromTabPanel & {
  id: string;
  label: string;
  type?: IColourMixinType;
  active: boolean | undefined;
  badge: number | undefined;
  shouldRender: boolean | undefined;
  onlyRenderWhenActive: boolean | undefined;
  tabStyle: React.CSSProperties; //TODO - Replace with {styles: {tab: React.CSSProperties}}
  permissionReference?: string;
  tooltip?: ComponentProps<typeof Tooltip>['content'];
};

const useStyles = makeStyles({
  container: {
    backgroundColor: p4Tokens.colorHeaderBackground,
  },
  text: {
    color: p4Tokens.colorHeaderForeground,
    '&:hover': {
      color: p4Tokens.colorHeaderHoverForeground,
    },
  },
  activeTabText: {
    color: p4Tokens.colorHeaderHoverForeground,
    fontWeight: 600,
    textDecoration: 'none',
    backgroundColor: '#faf9f8',
    borderBottom: '3px solid #3949ab',
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: '#faf9f8',
      borderBottom: '3px solid #3949ab',
    },
  },
});

const Tab = (props: ITabProps<unknown>) => {
  const handleClick = (event: any) => {
    event.preventDefault();
    props.activateTab(props.id, true);
  };

  const fullUserDetails = useFullUserDetails();
  const tabStyles = useStyles();

  const {
    active, // Indicates if tab is the default active tab
    badge,
    className,
    activateTab,
    isActiveTab,
    id,
    label,
    shouldRender,
    onlyRenderWhenActive,
    tabStyle,
    type,
    permissionReference,
    tooltip,
    ...propsToPass
  } = props;

  const permitted = useMemo(
    () => (permissionReference ? checkPermission({ app: { fullUserDetails } }, permissionReference) : true),
    [permissionReference]
  );

  if (!permitted || props.shouldRender === false) {
    return null;
  }

  const activeClass = props.isActiveTab ? `${tabStyles.activeTabText} ` : '';

  const content = (
    <div className={mergeClasses(styles[className], tabStyles.container)} style={tabStyle}>
      <a
        href="#"
        onClick={handleClick}
        className={mergeClasses(tabStyles.text, activeClass, styles[className])}
        tabIndex={-1}
        data-testid={`tab-${id}`}
        {...propsToPass}
      >
        {label} {badge ? <Badge type={type}>{badge}</Badge> : null}
      </a>
    </div>
  );
  if (tooltip) {
    return (
      <Tooltip content={tooltip} relationship="label">
        {content}
      </Tooltip>
    );
  }
  return content;
};

Tab.defaultProps = {
  tabStyle: {}, //Sales Lookup Modal has quite long named tabs, so this allows setting an easy width override for them all.
};

export default Tab;
